import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { getCsrfToken } from './api';
import { cookie } from './cookie';
import { getCurrentLanguage } from './language';

/**
 * Axios instance с автоматической настройкой заголовков для всех запросов
 * Автоматически добавляет:
 * - X-CSRFToken
 * - X-Requested-With
 * - Accept-Language
 * - Content-Type: application/json (если не FormData)
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: '/',
});

// Интерцептор запросов - автоматически добавляет заголовки
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Получаем CSRF токен
    const csrfToken = getCsrfToken() ?? String(cookie.get('csrftoken'));

    // Устанавливаем обязательные заголовки
    config.headers['X-CSRFToken'] = csrfToken;
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['Accept-Language'] = getCurrentLanguage();

    // Устанавливаем Content-Type только если это не FormData
    if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Интерцептор ответов - нормализация ошибок для tanstack query
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Нормализуем axios ошибки для tanstack query
    // Tanstack query ожидает ошибки с полем 'result.code' для аналитики (queryClient.ts)
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;

      // Извлекаем метаданные об ошибке для аналитики
      const url = error.config?.url || error.request?.responseURL || 'unknown';
      const method = error.config?.method?.toUpperCase() || 'unknown';
      const statusCode = error.response?.status;
      const fullUrl = error.config?.baseURL ? `${error.config.baseURL}${url}` : url;

      // Если сервер вернул структурированную ошибку с полем 'result' и 'result.code' - пробрасываем как есть
      // Это нужно для корректной работы аналитики в queryClient
      if (
        errorData &&
        typeof errorData === 'object' &&
        'result' in errorData &&
        errorData.result !== null &&
        typeof errorData.result === 'object' &&
        'code' in errorData.result
      ) {
        // Добавляем метаданные для аналитики
        const enrichedError = {
          ...errorData,
          _metadata: {
            url: fullUrl,
            method,
            statusCode,
          },
        };
        return Promise.reject(enrichedError);
      }

      // Если есть структурированная ошибка без кода (result: null) - тоже пробрасываем
      // queryClient обработает это как неструктурированную ошибку и отправит аналитику
      if (errorData && typeof errorData === 'object' && 'result' in errorData) {
        // Добавляем метаданные для аналитики
        const enrichedError = {
          ...errorData,
          _metadata: {
            url: fullUrl,
            method,
            statusCode,
          },
        };
        return Promise.reject(enrichedError);
      }

      // Иначе создаем стандартную Error с сообщением из ответа
      const errorMessage =
        (errorData && typeof errorData === 'object' && 'message' in errorData
          ? errorData.message
          : null) ||
        error.message ||
        'Request failed';

      // Создаем расширенную ошибку с метаданными
      const extendedError = new Error(String(errorMessage)) as Error & {
        _metadata?: { url: string; method: string; statusCode: number | null };
      };
      extendedError._metadata = {
        url: fullUrl,
        method,
        statusCode,
      };

      return Promise.reject(extendedError);
    }

    // Для других ошибок (сетевые, таймауты и т.д.) добавляем метаданные если возможно
    if (axios.isAxiosError(error)) {
      const url = error.config?.url || error.request?.responseURL || 'unknown';
      const method = error.config?.method?.toUpperCase() || 'unknown';
      const fullUrl = error.config?.baseURL ? `${error.config.baseURL}${url}` : url;

      const extendedError = new Error(error.message || 'Network error') as Error & {
        _metadata?: { url: string; method: string; statusCode: number | null };
      };
      extendedError._metadata = {
        url: fullUrl,
        method,
        statusCode: error.response?.status || null,
      };

      return Promise.reject(extendedError);
    }

    return Promise.reject(error);
  },
);
